import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Map from "@/assets/mapa-brasil-novo";

const statesData: Record<
  string,
  {
    name: string;
    capacidade: string;
  }
> = {
  SP: {
    name: "São Paulo",
    capacidade: "+190 MWp",
  },
  PR: {
    name: "Paraná",
    capacidade: "+120 MWp",
  },
  GO: {
    name: "Goiás",
    capacidade: "+75 MWp",
  },
  MT: {
    name: "Mato Grosso",
    capacidade: "+65 MWp",
  },
  RJ: {
    name: "Rio de Janeiro",
    capacidade: "+55 MWp",
  },
  PI: {
    name: "Piauí",
    capacidade: "+45 MWp",
  },
  PE: {
    name: "Pernambuco",
    capacidade: "+30 MWp",
  },
  RN: {
    name: "Rio Grande do Norte",
    capacidade: "+25 MWp",
  },
  MG: {
    name: "Minas Gerais",
    capacidade: "+25 MWp",
  },
  ES: {
    name: "Espírito Santo",
    capacidade: "+15 MWp",
  },
  SC: {
    name: "Santa Catarina",
    capacidade: "+15 MWp",
  },
  PA: {
    name: "Pará",
    capacidade: "+15 MWp",
  },
  MS: {
    name: "Mato Grosso do Sul",
    capacidade: "+15 MWp",
  },
  DF: {
    name: "Distrito Federal",
    capacidade: "+10 MWp",
  },
  CE: {
    name: "Ceará",
    capacidade: "+10 MWp",
  },
  AL: {
    name: "Alagoas",
    capacidade: "+10 MWp",
  },
  MA: {
    name: "Maranhão",
    capacidade: "+5 MWp",
  },
  TO: {
    name: "Tocantins",
    capacidade: "+5 MWp",
  },
  BA: {
    name: "Bahia",
    capacidade: "+5 MWp",
  },
  PB: {
    name: "Paraíba",
    capacidade: "+3 MWp",
  },
};

interface TooltipData {
  name: string;
  capacidade: string;
  x: number;
  y: number;
}
const BrazilInteractiveMap: React.FC = () => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const closeTooltip = () => {
    setTooltip(null);
  };

  const openTooltip = (data: TooltipData) => {
    if (data?.name === tooltip?.name) return closeTooltip();
    setTooltip(data);
  };

  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) return;

    const tooltipEl = tooltipRef.current;
    const containerRect = containerRef.current!.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    let x = tooltip.x;
    let y = tooltip.y;

    const padding = 12;

    x = clamp(
      x,
      padding + tooltipRect.width / 2,
      containerRect.width - tooltipRect.width / 2 - padding,
    );

    y = clamp(
      y - padding * 2,
      tooltipRect.height + padding,
      containerRect.height - padding,
    );

    tooltipEl.style.left = `${x}px`;
    tooltipEl.style.top = `${y}px`;
  }, [tooltip]);

  useEffect(() => {
    if (!tooltip) return;

    const handleScroll = () => closeTooltip();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tooltip]);

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center">
      <Map
        onMouseClick={(e, state) => {
          if (!state || !containerRef.current) {
            setTooltip(null);
            return closeTooltip();
          }

          const path = e.currentTarget;
          const rect = path.getBoundingClientRect();
          const containerRect = containerRef.current!.getBoundingClientRect();

          const x = rect.left + rect.width / 2 - containerRect.left;
          const y = rect.top + rect.height / 2 - containerRect.top;

          const { capacidade, name } = statesData[state];

          openTooltip({
            capacidade,
            name,
            x,
            y,
          });
        }}
      />
      {/* Tooltip */}
      {tooltip && (
        <div
          ref={tooltipRef}
          className="absolute z-50 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 min-w-[220px]"
          style={{
            transform: "translate(-50%, -110%)",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-thopen-yellow to-thopen-pink" />
            <h4 className="font-display font-bold text-gray-900 text-base">
              {tooltip?.name}
            </h4>
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="flex flex-wrap justify-center gap-6 mt-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gradient-to-r from-thopen-yellow to-thopen-pink" />
          <span className="text-sm text-gray-600">
            Estados com usinas Thopen
          </span>
        </div>
      </div>
    </div>
  );
};
export default BrazilInteractiveMap;
